/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Typography, Box, Divider, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Paper, alpha, useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import { useTheme } from '@emotion/react';
import SearchInput from '../../components/inputs/searchInput';
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';
import debounce from 'lodash.debounce';
import Popup from '../../components/popups/popup';
import DriveFileRenameOutlineOutlinedIcon from '@mui/icons-material/DriveFileRenameOutlineOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import RoleService from '../../services/roleService';
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import DeleteRoleModal from '../../components/modals/role/deleteRoleModal';
import ViewRoleModal from '../../components/modals/role/viewRoleModal';
import UpdateRoleBox from '../../components/boxes/role/updateRoleBox';
import AddRoleBox from '../../components/boxes/role/addRoleBox';
import ArrowUpwardOutlinedIcon from '@mui/icons-material/ArrowUpwardOutlined';
import ArrowDownwardOutlinedIcon from '@mui/icons-material/ArrowDownwardOutlined';
import OnOffSwitchForDeleted from '../../components/inputs/onOffSwitchForDeleted';

const Roles = () => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const isEn = i18n.language === 'en';
    const tableHeadingStyle = { fontWeight: 'bold', backgroundColor: theme.palette.customGrey.tableHeading, cursor: 'pointer' };
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [roles, setRoles] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedRole, setSelectedRole] = useState({});
    const [sortState, setSortState] = useState({ sortBy: '', order: '' });

    const [openUpdate, setOpenUpdate] = useState(false);
    const [openDelete, setOpenDelete] = useState(false);
    const [openView, setOpenView] = useState(false);
    const [deleted, setDeleted] = useState(false);

    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const tableContainerRef = useRef(null);
    const isFirstRender = useRef(true);

    const [showPopup, setShowPopup] = useState(false);
    const [popupMsg, setPopupMsg] = useState(false);
    const [popupType, setPopupType] = useState(false);
    const [isLoading, setIsLoading] = useState(false);


    const onSort = (sort) => {
        let order = '';
        if (sort === sortState.sortBy || sortState.sortBy === '') {
            switch (sortState.order) {
                case '':
                    order = 'asc';
                    break;
                case 'asc':
                    order = 'desc';
                    break;
                case 'desc':
                    order = '';
                    break;
                default:
                    order = '';
                    break
            }
        }

        setSortState({ sortBy: order === '' ? '' : sort, order });
    };

    const sortIcon = () => {
        let icon = <></>;
        switch (sortState.order) {
            case 'asc':
                icon = <ArrowDownwardOutlinedIcon fontSize="small" />;
                break;
            case 'desc':
                icon = <ArrowUpwardOutlinedIcon fontSize="small" />;
                break;
            default:
                icon = <ArrowDownwardOutlinedIcon fontSize="small" sx={{ color: 'transparent' }} />;
                break
        }

        return icon;
    };

    const handleOpenView = (role) => {
        setSelectedRole(role);
        setOpenView(true);
    };;
    const handleCloseView = () => setOpenView(false);

    const handleOpenUpdate = (role) => {
        setSelectedRole(role);
        setOpenUpdate(true);
    };

    const handleReload = async () => {
        setOpenUpdate(false);
        setHasMore(true);
        setPage(1);
        setSearchTerm('');
        await fetchRoles();
    };

    const handleOpenDelete = (role) => {
        setSelectedRole(role);
        setOpenDelete(true);
    };
    const handleCloseDelete = async ({ isReaload }) => {
        setOpenDelete(false);
        if (isReaload) {
            setHasMore(true);
            setPage(1);
            setSearchTerm('');
            await fetchRoles();
        }
    };


    const fetchRoles = async (page = 1, name = '') => {
        if (isLoading) return;
        setIsLoading(true);
        try {
            const filters = { name, ...sortState, deleted }
            const data = await RoleService.fetch(page, filters);
            if (data.length === 0) {
                setHasMore(false);
                if (page === 1) {
                    setRoles([]);
                }
            } else {
                setRoles((prevs) => (page === 1 ? data : [...prevs, ...data]));
            }
        } catch (error) {
            console.error('Error fetching roles:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const debouncedFetchRoles = useMemo(() =>
        debounce((name) => {
            setPage(1);
            setHasMore(true);
            fetchRoles(1, name);
        }, 1000),
        []
    );

    const handleSearchChange = (value) => {
        setSearchTerm(value);
        debouncedFetchRoles(value);
    };

    const resetFilter = () => {
        setSearchTerm('');
        setHasMore(true);
        if (page !== 1) setPage(1);
        else fetchRoles(1, '');
    };

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }

        if (page === 1) fetchRoles(page, searchTerm);
    }, [page]);

    useEffect(() => {
        if (!isFirstRender.current) resetFilter();
    }, [sortState, deleted]);

    const handleScroll = (event) => {
        const container = event.currentTarget;

        if (container.scrollTop + container.clientHeight >= container.scrollHeight - 50) {
            if (hasMore && !isLoading) {
                fetchRoles(page + 1, searchTerm);
                setPage(prevPage => prevPage + 1);
            }
        }
    };
    return (
        <Box sx={{
            p: 2,
            backgroundColor: theme.palette.secondary.main,
        }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <DeleteRoleModal open={openDelete} handleClose={handleCloseDelete} setPopupMsg={setPopupMsg}
                    setPopupType={setPopupType}
                    setShowPopup={setShowPopup} role={selectedRole} />
                <ViewRoleModal open={openView} handleClose={handleCloseView} roleId={selectedRole.id} />
            </Box>
            {showPopup && (
                <Popup
                    text={popupMsg}
                    type={popupType}
                    onClose={() => setShowPopup(false)}
                />
            )}

            {isTablet && <Box>
                {
                    (selectedRole && openUpdate) ?
                        <UpdateRoleBox handleReload={handleReload} setPopupMsg={setPopupMsg}
                            setPopupType={setPopupType}
                            setShowPopup={setShowPopup} roleId={selectedRole.id} />
                        :
                        <AddRoleBox handleReload={handleReload} setPopupMsg={setPopupMsg}
                            setPopupType={setPopupType}
                            setShowPopup={setShowPopup} />
                }
            </Box>}

            {/* Filter row */}
            {
                isMobile ?
                    <Box
                        sx={{
                            my: 2,
                            border: 1,
                            borderColor: theme.palette.customGrey.borderInput,
                            backgroundColor: 'white',
                            borderRadius: 1,
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', height: 50 }}>
                            <Box sx={{ px: 2 }}>
                                <FilterAltOutlinedIcon />
                            </Box>
                            <Divider orientation="vertical" />
                            <Box width={isTablet ? '60%' : '70%'}>
                                <SearchInput placeholder={t('name')} onChange={handleSearchChange} value={searchTerm} />
                            </Box>
                        </Box>
                        <Divider />

                        <Box sx={{ display: 'flex', alignItems: 'center', height: 50 }}>
                            <Box sx={{
                                ml: isEn ? 3 : 0
                            }}>
                                <OnOffSwitchForDeleted
                                    checkDeleted={deleted}
                                    setCheckDeleted={setDeleted} />
                            </Box>
                            <Divider orientation="vertical" sx={{ mr: isEn ? 0 : 3 }} />
                            <Box
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    color: theme.palette.text.red,
                                    px: 2,
                                    cursor: 'pointer',
                                }}
                                onClick={resetFilter}
                            >
                                <ReplayOutlinedIcon />
                                <Box width={3}></Box>
                                <Typography variant="subtitle2">{t('resetFilter')}</Typography>
                            </Box>
                        </Box>
                    </Box>
                    :
                    <Box
                        sx={{
                            my: 2,
                            display: 'flex',
                            alignItems: 'center',
                            border: 1,
                            borderColor: theme.palette.customGrey.borderInput,
                            backgroundColor: 'white',
                            borderRadius: 1,
                            height: 60,
                        }}
                    >
                        <Box sx={{ px: 2 }}>
                            <FilterAltOutlinedIcon />
                        </Box>
                        <Divider orientation="vertical" />
                        <Box width={isTablet ? '60%' : '70%'}>
                            <SearchInput placeholder={t('name')} onChange={handleSearchChange} value={searchTerm} />
                        </Box>
                        <Divider orientation="vertical" sx={{ mr: isEn ? 3 : 0 }} />

                        <OnOffSwitchForDeleted
                            checkDeleted={deleted}
                            setCheckDeleted={setDeleted} />
                        <Divider orientation="vertical" sx={{ mr: isEn ? 0 : 3 }} />
                        <Box
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                color: theme.palette.text.red,
                                px: 2,
                                cursor: 'pointer',
                            }}
                            onClick={resetFilter}
                        >
                            <ReplayOutlinedIcon />
                            <Box width={3}></Box>
                            <Typography variant="subtitle2">{t('resetFilter')}</Typography>
                        </Box>
                    </Box>
            }


            <Box sx={{
                display: 'flex'
            }}>
                {!isTablet && <Box sx={{
                    width: '50%'
                }}>
                    {
                        (selectedRole && openUpdate) ?
                            <UpdateRoleBox handleReload={handleReload} setPopupMsg={setPopupMsg}
                                setPopupType={setPopupType}
                                setShowPopup={setShowPopup} roleId={selectedRole.id} />
                            :
                            <AddRoleBox handleReload={handleReload} setPopupMsg={setPopupMsg}
                                setPopupType={setPopupType}
                                setShowPopup={setShowPopup} />
                    }
                </Box>}

                {!isTablet && <Box width={10}></Box>}

                {/* Scrollable Table with Sticky Header */}
                <TableContainer
                    component={Paper}
                    ref={tableContainerRef}
                    onScroll={handleScroll}
                    sx={{
                        boxShadow: 'none',
                        border: 1,
                        borderColor: theme.palette.customGrey.borderInput,
                        borderRadius: 2,
                        height: 'calc(100vh - 180px)',
                        overflowY: 'auto',
                        width: isTablet ? '100%' : '50%'
                    }}
                >
                    <Table stickyHeader aria-label="simple table">
                        <TableHead
                            sx={{
                                position: 'sticky',
                                top: 0,
                                zIndex: 1,
                            }}
                        >
                            <TableRow>
                                <TableCell align={isEn ? 'left' : 'right'}
                                    onClick={() => onSort('id')} sx={tableHeadingStyle}>
                                    <Box sx={{ display: 'flex' }} >
                                        {t('id')}
                                        {sortState.sortBy === 'id' && sortIcon()}
                                    </Box>
                                </TableCell>
                                <TableCell align={isEn ? 'left' : 'right'} sx={tableHeadingStyle}
                                    onClick={() => onSort('name')}>
                                    <Box sx={{ display: 'flex' }} >
                                        {t('name')}
                                        {sortState.sortBy === 'name' && sortIcon()}
                                    </Box></TableCell>
                                <TableCell align={isEn ? 'left' : 'right'} sx={tableHeadingStyle}
                                    onClick={() => onSort('superAdmin')}>
                                    <Box sx={{ display: 'flex' }} >
                                        {t('superAdmin')}
                                        {sortState.sortBy === 'superAdmin' && sortIcon()}
                                    </Box></TableCell>
                                <TableCell align={isEn ? 'left' : 'right'} sx={{ ...tableHeadingStyle, cursor: 'auto' }}>
                                    {t('actions')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {roles.map((role, index) => (
                                <TableRow
                                    key={role.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                    <TableCell align={isEn ? 'left' : 'right'} component="th" scope="row">{role.id}</TableCell>
                                    <TableCell align={isEn ? 'left' : 'right'}>{role.name}</TableCell>
                                    <TableCell align={isEn ? 'left' : 'right'}>
                                        <Box
                                            sx={{
                                                bgcolor: alpha(
                                                    role.superAdmin ? theme.palette.green.box : theme.palette.purple.box, 0.2),
                                                width: 100,
                                                justifyContent: 'center',
                                                display: 'flex',
                                                py: 0.8,
                                                borderRadius: 0.8,
                                            }}
                                        >
                                            <Box sx={{
                                                fontWeight: 'bold',
                                                color: role.superAdmin ? theme.palette.green.box : theme.palette.purple.box,
                                            }}>
                                                {role.superAdmin ? t('yes') : t('no')}</Box>
                                        </Box>
                                    </TableCell>
                                    <TableCell align={isEn ? 'left' : 'right'}>
                                        <Box sx={{
                                            display: 'inline-flex',
                                            border: 1,
                                            borderColor: theme.palette.customGrey.borderInput,
                                            backgroundColor: theme.palette.secondary.main,
                                            borderRadius: 1,
                                        }}>
                                            <Box sx={{
                                                px: 1.5,
                                                pt: 0.6,
                                                pb: 0.25,
                                                cursor: 'pointer'
                                            }} onClick={() => handleOpenView(role)}>
                                                <RemoveRedEyeOutlinedIcon fontSize='small'
                                                    sx={{ color: theme.palette.text.primary }} />
                                            </Box>
                                            <Divider orientation="vertical" flexItem />
                                            {role.deletedAt === null && <Box sx={{
                                                px: 1.5,
                                                pt: 0.4,
                                                pb: 0.25,
                                                cursor: role.superAdmin ? 'not-allowed' : 'pointer',
                                                pointerEvents: role.superAdmin ? 'none' : 'auto',
                                                opacity: role.superAdmin ? 0.5 : 1,
                                            }} onClick={!role.superAdmin ? () => handleOpenUpdate(role) : undefined}>
                                                <DriveFileRenameOutlineOutlinedIcon fontSize='small'
                                                    sx={{ color: theme.palette.text.primary }} />
                                            </Box>}
                                            {role.deletedAt === null && <Divider orientation="vertical" flexItem />}
                                            <Box sx={{
                                                px: 1.5,
                                                pt: 0.4,
                                                pb: 0.25,
                                                cursor: role.superAdmin ? 'not-allowed' : 'pointer',
                                                pointerEvents: role.superAdmin ? 'none' : 'auto',
                                                opacity: role.superAdmin ? 0.5 : 1,
                                            }} onClick={!role.superAdmin ? () => handleOpenDelete(role) : undefined}>
                                                {role.deletedAt === null ?
                                                    <DeleteOutlineOutlinedIcon fontSize='small'
                                                        sx={{ color: theme.palette.red.bg }} />
                                                    :
                                                    <ReplayOutlinedIcon fontSize='small'
                                                        sx={{ color: theme.palette.green.bg }} />
                                                }
                                            </Box>

                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

            </Box>

        </Box>
    );
};

export default Roles;
